import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { MapPin, Calendar, Trophy, CheckCircle, Loader2, Bell, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_chennai-finrun/artifacts/eabq3wxl_FINRUN%20LOGO%20PNG%202.png";

// Registration is now OPEN
const REGISTRATION_OPEN = true;

// Early Bird Configuration
const EARLY_BIRD_DEADLINE = new Date("2026-09-05T23:59:59");
const isEarlyBirdActive = () => new Date() < EARLY_BIRD_DEADLINE;
const getEarlyBirdDiscount = () => {
  if (new Date() < EARLY_BIRD_DEADLINE) {
    return 0.20; // 20% Super Early Bird until Sep 5
  }
  return 0.10; // 10% Regular Early Bird after Sep 5
};

// Event-specific pricing - Only Hyderabad 2027 (3rd Edition)
const EVENT_PRICING = {
  "hyderabad-2027": {
    name: "The FinRun Hyderabad 2027",
    edition: "3rd Edition",
    date: "February 7, 2027",
    location: "Gachibowli Stadium, Hyderabad",
    regular: { "3K": 799, "5K": 999, "10K": 1199 }
  }
};

// Get price for a category and event (applies early bird if active)
const getPrice = (eventId, category) => {
  const event = EVENT_PRICING[eventId];
  if (!event) return 799; // default
  const regularPrice = event.regular[category];
  const discount = getEarlyBirdDiscount();
  return Math.round(regularPrice * (1 - discount));
};

// Get regular price (for showing strikethrough)
const getRegularPrice = (eventId, category) => {
  const event = EVENT_PRICING[eventId];
  if (!event) return 799;
  return event.regular[category];
};

// Early Bird Countdown Component
const EarlyBirdCountdown = ({ deadline }) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = new Date(deadline) - new Date();
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [deadline]);

  return (
    <div className="flex items-center gap-2 text-white/90 text-sm">
      <span className="bg-white/20 px-2 py-1 rounded">{timeLeft.days}d</span>
      <span>:</span>
      <span className="bg-white/20 px-2 py-1 rounded">{String(timeLeft.hours).padStart(2, '0')}h</span>
      <span>:</span>
      <span className="bg-white/20 px-2 py-1 rounded">{String(timeLeft.minutes).padStart(2, '0')}m</span>
      <span>:</span>
      <span className="bg-white/20 px-2 py-1 rounded">{String(timeLeft.seconds).padStart(2, '0')}s</span>
    </div>
  );
};

const RegisterPage = () => {
  const { event: eventParam } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [ticketNumber, setTicketNumber] = useState("");
  const [razorpayConfigured, setRazorpayConfigured] = useState(false);
  const [events, setEvents] = useState([]);
  
  // Default to hyderabad-2027 (3rd Edition)
  const defaultEvent = eventParam || "hyderabad-2027";
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    gender: "",
    age: "",
    race_category: "5K",
    event: defaultEvent,
    emergency_contact_name: "",
    emergency_contact_phone: "",
    tshirt_size: "",
    blood_group: "",
    medical_conditions: ""
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, configRes] = await Promise.all([
          axios.get(`${API}/events`),
          axios.get(`${API}/config/razorpay`)
        ]);
        setEvents(eventsRes.data.events);
        setRazorpayConfigured(configRes.data.configured);
      } catch (e) {
        console.error("Error fetching data:", e);
      }
    };
    fetchData();
  }, []);

  // If registration is closed, show coming soon page
  if (!REGISTRATION_OPEN) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Navbar />
        <main className="pt-32 pb-20">
          <div className="container-custom">
            <div className="max-w-2xl mx-auto text-center">
              {/* Logo */}
              <img 
                src={LOGO_URL} 
                alt="The FinRun" 
                className="h-32 md:h-40 w-auto mx-auto mb-8"
              />
              
              {/* Coming Soon Card */}
              <Card className="shadow-xl border-0">
                <CardContent className="p-12">
                  <div className="w-20 h-20 bg-[#FFD700]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Bell className="w-10 h-10 text-[#FFD700]" />
                  </div>
                  
                  <h1 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-[#0A0A0A] mb-4">
                    Registration Opening Soon!
                  </h1>
                  
                  <p className="text-[#4B5563] text-lg mb-8">
                    We're finalizing the dates for The FinRun 2026 editions in Chennai and Hyderabad. 
                    Stay tuned for the official announcement!
                  </p>
                  
                  {/* Event Cards */}
                  <div className="grid md:grid-cols-2 gap-4 mb-8">
                    <div className="bg-[#FFD700]/10 rounded-2xl p-6 border border-[#FFD700]/20">
                      <h3 className="font-bold text-[#0A0A0A] mb-1">Chennai 2026</h3>
                      <p className="text-[#4B5563] text-sm">Dates Announcing Soon</p>
                    </div>
                    <div className="bg-[#FF3B30]/10 rounded-2xl p-6 border border-[#FF3B30]/20">
                      <h3 className="font-bold text-[#0A0A0A] mb-1">Hyderabad 2026</h3>
                      <p className="text-[#4B5563] text-sm">Dates Announcing Soon</p>
                    </div>
                  </div>
                  
                  {/* CTA */}
                  <div className="space-y-4">
                    <Link to="/#contact">
                      <Button className="btn-primary w-full py-6 text-lg">
                        <Bell className="w-5 h-5 mr-2" />
                        Notify Me When Registration Opens
                      </Button>
                    </Link>
                    <Link to="/">
                      <Button variant="outline" className="w-full py-6 text-lg rounded-full">
                        <ArrowLeft className="w-5 h-5 mr-2" />
                        Back to Home
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
              
              {/* Categories Preview */}
              <div className="mt-12">
                <p className="text-[#4B5563] mb-6">Available categories when registration opens:</p>
                <div className="flex justify-center gap-4 flex-wrap">
                  <span className="bg-[#FFD700]/20 text-[#0A0A0A] px-6 py-3 rounded-full font-semibold">
                    3K Fun Run - ₹799
                  </span>
                  <span className="bg-[#FF3B30]/20 text-[#0A0A0A] px-6 py-3 rounded-full font-semibold">
                    5K Timed - ₹999
                  </span>
                  <span className="bg-[#007AFF]/20 text-[#0A0A0A] px-6 py-3 rounded-full font-semibold">
                    10K Timed - ₹1199
                  </span>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const selectedEventData = EVENT_PRICING[formData.event];
  const selectedPrice = getPrice(formData.event, formData.race_category);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async (registrationId) => {
    const loaded = await loadRazorpayScript();
    if (!loaded) {
      toast.error("Failed to load payment gateway");
      return;
    }

    try {
      const orderRes = await axios.post(`${API}/payments/create-order`, {
        registration_id: registrationId
      });

      const options = {
        key: orderRes.data.key_id,
        amount: orderRes.data.amount,
        currency: orderRes.data.currency,
        order_id: orderRes.data.order_id,
        name: "The FinRun",
        description: `${formData.race_category} Run - ${selectedEventData?.name || "The FinRun"}`,
        image: LOGO_URL,
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            
            if (verifyRes.data.success) {
              setTicketNumber(verifyRes.data.ticket_number);
              setRegistrationComplete(true);
              toast.success("Registration successful!");
            }
          } catch (error) {
            toast.error("Payment verification failed");
          }
          setPaymentProcessing(false);
        },
        prefill: {
          name: `${formData.first_name} ${formData.last_name}`,
          email: formData.email,
          contact: formData.phone
        },
        theme: {
          color: "#FF3B30"
        },
        modal: {
          ondismiss: () => {
            setPaymentProcessing(false);
            toast.info("Payment cancelled");
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error("Failed to create payment order");
      setPaymentProcessing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        age: parseInt(formData.age)
      };

      const response = await axios.post(`${API}/registrations`, payload);
      
      if (razorpayConfigured) {
        setPaymentProcessing(true);
        await handlePayment(response.data.id);
      } else {
        // Demo mode - show success without payment
        setTicketNumber(`FR26-${formData.event.toUpperCase().slice(0, 3)}-DEMO${Math.random().toString(36).substring(7).toUpperCase()}`);
        setRegistrationComplete(true);
        toast.success("Registration saved! (Payment gateway not configured - Demo mode)");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  if (registrationComplete) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Navbar />
        <div className="container-custom section-spacing pt-32">
          <Card className="max-w-2xl mx-auto text-center" data-testid="registration-success">
            <CardContent className="p-12">
              <img 
                src={LOGO_URL} 
                alt="The FinRun" 
                className="h-20 w-auto mx-auto mb-6"
              />
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-4xl font-black text-[#0A0A0A] mb-4 font-['Outfit']">
                Registration Complete!
              </h1>
              <p className="text-[#4B5563] text-lg mb-6">
                Welcome to The FinRun! You're now part of India's largest cyber awareness movement.
              </p>
              <div className="bg-[#111827] text-white p-6 rounded-2xl mb-8">
                <p className="text-sm text-gray-400 mb-2">Your Ticket Number</p>
                <p className="text-3xl font-bold tracking-wider" data-testid="ticket-number">{ticketNumber}</p>
              </div>
              <div className="grid grid-cols-2 gap-4 text-left mb-8">
                <div>
                  <p className="text-sm text-[#9CA3AF]">Event</p>
                  <p className="font-semibold">{selectedEventData?.name || "The FinRun"}</p>
                </div>
                <div>
                  <p className="text-sm text-[#9CA3AF]">Category</p>
                  <p className="font-semibold">{formData.race_category} Run</p>
                </div>
                <div>
                  <p className="text-sm text-[#9CA3AF]">Date</p>
                  <p className="font-semibold">{selectedEventData?.date || "TBA"}</p>
                </div>
                <div>
                  <p className="text-sm text-[#9CA3AF]">Location</p>
                  <p className="font-semibold">{selectedEventData?.location || "TBA"}</p>
                </div>
              </div>
              <p className="text-sm text-[#4B5563] mb-6">
                A confirmation email has been sent to <strong>{formData.email}</strong>
              </p>
              <Button 
                onClick={() => navigate("/")} 
                className="btn-primary"
                data-testid="back-home-btn"
              >
                Back to Home
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="register-page">
      <Navbar />
      
      {/* Hero Banner */}
      <div className="bg-[#111827] pt-24 pb-16">
        <div className="container-custom">
          <div className="text-center">
            <p className="text-[#FF3B30] text-sm uppercase tracking-[0.2em] font-bold mb-4">
              Register Now
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 font-['Outfit']">
              Join The <span className="text-[#FFD700]">FinRun</span>
            </h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Be part of India's First & Largest Cyber Awareness Run. 
              Every stride makes a difference in the fight against cybercrime.
            </p>
          </div>
        </div>
      </div>

      <div className="container-custom py-16">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl font-bold font-['Outfit']">
                  Runner Registration
                </CardTitle>
                <CardDescription>
                  Fill in your details to secure your spot
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Super Early Bird Banner with Countdown */}
                  {isEarlyBirdActive() && (
                    <div className="bg-gradient-to-r from-[#00C853] to-[#00E676] rounded-xl p-4 text-white">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xl">🎉</span>
                        <span className="font-bold text-lg">SUPER EARLY BIRD — {Math.round(getEarlyBirdDiscount() * 100)}% OFF!</span>
                      </div>
                      <p className="text-sm text-white/90 mb-2">
                        Offer ends September 5th at 11:59 PM. Save up to ₹240!
                      </p>
                      <EarlyBirdCountdown deadline={EARLY_BIRD_DEADLINE} />
                    </div>
                  )}

                  {/* Event Selection */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="event">Select Event *</Label>
                      <Select 
                        value={formData.event} 
                        onValueChange={(v) => handleChange("event", v)}
                      >
                        <SelectTrigger data-testid="event-select">
                          <SelectValue placeholder="Select event" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(EVENT_PRICING).map(([id, event]) => (
                            <SelectItem key={id} value={id}>
                              {event.name} - {event.date}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="race_category">Race Category *</Label>
                      <Select 
                        value={formData.race_category} 
                        onValueChange={(v) => handleChange("race_category", v)}
                      >
                        <SelectTrigger data-testid="category-select">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="3K">
                            3K Fun Run - ₹{getPrice(formData.event, "3K")} {isEarlyBirdActive() && <span className="text-gray-400 line-through ml-1">₹{getRegularPrice(formData.event, "3K")}</span>}
                          </SelectItem>
                          <SelectItem value="5K">
                            5K Timed Run - ₹{getPrice(formData.event, "5K")} {isEarlyBirdActive() && <span className="text-gray-400 line-through ml-1">₹{getRegularPrice(formData.event, "5K")}</span>} (Cash Prizes)
                          </SelectItem>
                          <SelectItem value="10K">
                            10K Timed Run - ₹{getPrice(formData.event, "10K")} {isEarlyBirdActive() && <span className="text-gray-400 line-through ml-1">₹{getRegularPrice(formData.event, "10K")}</span>} (Cash Prizes)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Personal Info */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input 
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => handleChange("first_name", e.target.value)}
                        required
                        data-testid="first-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input 
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => handleChange("last_name", e.target.value)}
                        required
                        data-testid="last-name-input"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input 
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        required
                        data-testid="email-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone *</Label>
                      <Input 
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        required
                        data-testid="phone-input"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="gender">Gender *</Label>
                      <Select 
                        value={formData.gender} 
                        onValueChange={(v) => handleChange("gender", v)}
                      >
                        <SelectTrigger data-testid="gender-select">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="age">Age *</Label>
                      <Input 
                        id="age"
                        type="number"
                        min="5"
                        max="100"
                        value={formData.age}
                        onChange={(e) => handleChange("age", e.target.value)}
                        required
                        data-testid="age-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="tshirt_size">T-Shirt Size *</Label>
                      <Select 
                        value={formData.tshirt_size} 
                        onValueChange={(v) => handleChange("tshirt_size", v)}
                      >
                        <SelectTrigger data-testid="tshirt-select">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="XS">XS</SelectItem>
                          <SelectItem value="S">S</SelectItem>
                          <SelectItem value="M">M</SelectItem>
                          <SelectItem value="L">L</SelectItem>
                          <SelectItem value="XL">XL</SelectItem>
                          <SelectItem value="XXL">XXL</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Emergency Contact */}
                  <div className="border-t pt-6">
                    <h3 className="font-semibold mb-4">Emergency Contact</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="emergency_contact_name">Contact Name *</Label>
                        <Input 
                          id="emergency_contact_name"
                          value={formData.emergency_contact_name}
                          onChange={(e) => handleChange("emergency_contact_name", e.target.value)}
                          required
                          data-testid="emergency-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="emergency_contact_phone">Contact Phone *</Label>
                        <Input 
                          id="emergency_contact_phone"
                          type="tel"
                          value={formData.emergency_contact_phone}
                          onChange={(e) => handleChange("emergency_contact_phone", e.target.value)}
                          required
                          data-testid="emergency-phone-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Medical Info */}
                  <div className="border-t pt-6">
                    <h3 className="font-semibold mb-4">Medical Information (Optional)</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="blood_group">Blood Group</Label>
                        <Select 
                          value={formData.blood_group} 
                          onValueChange={(v) => handleChange("blood_group", v)}
                        >
                          <SelectTrigger data-testid="blood-group-select">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="A+">A+</SelectItem>
                            <SelectItem value="A-">A-</SelectItem>
                            <SelectItem value="B+">B+</SelectItem>
                            <SelectItem value="B-">B-</SelectItem>
                            <SelectItem value="AB+">AB+</SelectItem>
                            <SelectItem value="AB-">AB-</SelectItem>
                            <SelectItem value="O+">O+</SelectItem>
                            <SelectItem value="O-">O-</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2 mt-4">
                      <Label htmlFor="medical_conditions">Medical Conditions</Label>
                      <Textarea 
                        id="medical_conditions"
                        placeholder="Any medical conditions we should be aware of..."
                        value={formData.medical_conditions}
                        onChange={(e) => handleChange("medical_conditions", e.target.value)}
                        data-testid="medical-conditions-input"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary text-lg py-6"
                    disabled={loading || paymentProcessing}
                    data-testid="submit-registration-btn"
                  >
                    {loading || paymentProcessing ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {paymentProcessing ? "Processing Payment..." : "Processing..."}
                      </>
                    ) : (
                      `Pay ₹${selectedPrice} & Register`
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Event Summary */}
            <Card className="border-0 shadow-xl bg-[#111827] text-white">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4 font-['Outfit']">Event Summary</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-[#FFD700] mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-400">Date</p>
                      <p className="font-semibold">{selectedEventData?.date || "TBA"}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-[#FF3B30] mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-400">Location</p>
                      <p className="font-semibold">{selectedEventData?.location || "TBA"}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Trophy className="w-5 h-5 text-[#007AFF] mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-400">Category</p>
                      <p className="font-semibold">{formData.race_category} Run</p>
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-700 mt-6 pt-6">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Registration Fee</span>
                    <div className="text-right">
                      {isEarlyBirdActive() ? (
                        <>
                          <span className="text-2xl font-bold text-[#00E676]">₹{selectedPrice}</span>
                          <span className="text-sm text-gray-500 line-through ml-2">₹{getRegularPrice(formData.event, formData.race_category)}</span>
                        </>
                      ) : (
                        <span className="text-2xl font-bold text-[#FFD700]">₹{selectedPrice}</span>
                      )}
                    </div>
                  </div>
                  {isEarlyBirdActive() && (
                    <p className="text-xs text-[#00E676] mt-2 text-right">
                      🎉 You save ₹{getRegularPrice(formData.event, formData.race_category) - selectedPrice} with {getEarlyBirdDiscount() === 0.20 ? 'Super ' : ''}Early Bird!
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* What's Included */}
            <Card className="border-0 shadow-xl">
              <CardContent className="p-6">
                <h3 className="text-xl font-bold mb-4 font-['Outfit']">What's Included</h3>
                <ul className="space-y-3">
                  {[
                    "Official Race BIB",
                    "Event T-Shirt",
                    "Finisher Medal",
                    "Goodie Bag",
                    "E-Certificate",
                    "Refreshments",
                    "Medical Support",
                    ...(formData.race_category !== "3K" ? ["Chip Timing", "Cash Prizes for Winners"] : [])
                  ].map((item) => (
                    <li key={item} className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-[#4B5563]">{item}</span>
                    </li>
                  ))}
                </ul>
                {formData.race_category !== "3K" && (
                  <div className="mt-4 bg-[#007AFF]/10 border border-[#007AFF]/20 rounded-xl p-3">
                    <p className="text-xs text-[#007AFF] font-semibold text-center">
                      TIMED RUN - Chip timing with cash prizes for winners!
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Past Event Image */}
            <Card className="border-0 shadow-xl overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_chennai-finrun/artifacts/97tk5mk3_DR6A3753.JPG"
                alt="The FinRun Participants"
                className="w-full h-auto"
              />
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RegisterPage;
